import { View, Text, Pressable } from 'react-native';
import { useRouter } from 'expo-router';
import Animated, { FadeInDown } from 'react-native-reanimated';
import { Sparkles, ArrowRight, X } from 'lucide-react-native';
import { useThemeColors } from '@/lib/useThemeColors';
import { useAuth } from '@/lib/supabase';
import { useOnboardingBannerStore } from '@/lib/state/onboarding-banner-store';
import {
  shouldShowOnboardingBanner,
  computeOnboardingProgress,
} from '@/lib/onboarding/banner-progress';
import { routeForStep } from '@/lib/onboarding/resolve-route';

const TEAL = '#00D9D9';
const TEAL_INK = '#0A1628';

/**
 * Resume nudge shown on the founder/investor dashboards when onboarding was
 * deferred ("Save & Exit") but not completed (Notion #67). Deep-links to the
 * persisted resume step; dismissible for the session (not persisted).
 * Renders nothing when onboarding is complete or the profile hasn't loaded.
 */
export function OnboardingIncompleteBanner() {
  const router = useRouter();
  const colors = useThemeColors();
  const { profile } = useAuth();
  const dismissed = useOnboardingBannerStore((s) => s.dismissed);
  const dismiss = useOnboardingBannerStore((s) => s.dismiss);

  const role = profile?.role as string | undefined;
  const visible =
    !dismissed &&
    shouldShowOnboardingBanner({
      onboardingCompleted: !!profile?.onboarding_completed,
      hasProfile: !!profile,
      role,
    });
  if (!visible) return null;

  // isInvitee is not resolvable from the profile alone; default to the full
  // flow (matches src/app/index.tsx's routing default).
  const progress = computeOnboardingProgress(profile?.onboarding_step ?? null, false);
  const donePct = Math.round(((progress.current - 1) / progress.total) * 100);
  const resumeRoute = routeForStep(role as 'startup' | 'investor', progress.resumeStep);

  return (
    <Animated.View entering={FadeInDown.duration(400)} style={{ paddingHorizontal: 20, paddingTop: 16 }}>
      <View
        style={{
          borderRadius: 16,
          backgroundColor: colors.surface.primary,
          borderWidth: 1,
          borderColor: 'rgba(0,217,217,0.28)',
          padding: 16,
        }}
      >
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
          <View
            style={{
              width: 40,
              height: 40,
              borderRadius: 12,
              backgroundColor: 'rgba(0,217,217,0.12)',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Sparkles size={20} color={TEAL} />
          </View>
          <View style={{ flex: 1 }}>
            <Text numberOfLines={1} style={{ color: colors.text.primary, fontWeight: '700', fontSize: 15 }}>
              Finish setting up your profile
            </Text>
            <Text numberOfLines={1} style={{ color: colors.text.tertiary, fontSize: 13, marginTop: 2 }}>
              Step {progress.current} of {progress.total} · Next: {progress.resumeLabel}
            </Text>
          </View>
          <View style={{ width: 32, height: 32, borderRadius: 8, overflow: 'hidden' }}>
            <Pressable
              onPress={dismiss}
              accessibilityRole="button"
              accessibilityLabel="Dismiss setup reminder"
              style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}
            >
              <X size={18} color={colors.text.tertiary} />
            </Pressable>
          </View>
        </View>

        {/* Progress track */}
        <View
          style={{
            height: 6,
            borderRadius: 3,
            backgroundColor: colors.surface.tertiary,
            marginTop: 14,
            overflow: 'hidden',
          }}
        >
          <View style={{ height: '100%', width: `${donePct}%`, backgroundColor: TEAL, borderRadius: 3 }} />
        </View>

        {/* Resume CTA — View owns dimensions (pressable-sizing rule) */}
        <View style={{ height: 44, borderRadius: 10, backgroundColor: TEAL, overflow: 'hidden', marginTop: 14 }}>
          <Pressable
            onPress={() => router.push(resumeRoute as never)}
            accessibilityRole="button"
            accessibilityLabel="Resume profile setup"
            style={{ flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6 }}
          >
            <Text style={{ color: TEAL_INK, fontWeight: '700', fontSize: 15 }}>Resume setup</Text>
            <ArrowRight size={17} color={TEAL_INK} />
          </Pressable>
        </View>
      </View>
    </Animated.View>
  );
}
